<template>
  <div
    class="tag-card"
    :class="[
      `tag-card--${poolType}`,
      { 'tag-card--sort-mode': sortMode }
    ]"
    :draggable="sortMode"
    @dragstart="onDragStart"
    @dragover="onDragOver"
    @drop="onDrop"
    @dragend="onDragEnd"
  >
    <div class="tag-card__left">
      <div class="tag-card__rank">
        <i v-if="sortMode" class="tag-card__drag-handle el-icon-rank"></i>
        <input
          v-if="sortMode"
          ref="rankInput"
          type="number"
          class="tag-card__rank-input"
          :value="index + 1"
          @change="onJumpTo($event)"
        />
        <span v-else class="tag-card__rank-text">#{{ index + 1 }}</span>
      </div>
      <div class="tag-card__info">
        <div class="tag-card__name-row">
          <h3 class="tag-card__name">{{ tag.name }}</h3>
          <span v-if="poolType !== 'general'" class="tag-card__priority-badge">优先级: {{ tag.priority }}</span>
          <span v-if="sourceBadge" class="tag-card__source-badge" :class="sourceBadgeClass">{{ sourceBadge }}</span>
        </div>
        <div class="tag-card__meta">
          <p class="tag-card__code"></p>
          <span class="tag-card__parent">{{ parentDisplay }}</span>
        </div>
      </div>
    </div>

    <div v-if="poolType === 'company'" class="tag-card__strategy">
      <el-button
        :type="strategyBtnType"
        size="mini"
        round
        @click="$emit('open-strategy', tag)"
      >{{ strategyLabel }}</el-button>
    </div>

    <div v-if="poolType === 'company'" class="tag-card__type-status">
      <span class="tag-card__type-badge">{{ tag.type }}</span>
      <span class="tag-card__status" :class="{ 'tag-card__status--active': isActive }">
        <i class="tag-card__status-dot" :class="{ 'tag-card__status-dot--active': isActive }"></i>
        {{ isActive ? '启用中' : '已停用' }}
      </span>
      <p class="tag-card__desc-id">{{ tag.caseStatusStr }}|{{ tag.contactStatusStr }}</p>
    </div>
    <div v-if="poolType !== 'company'" class="tag-card__type-status">
      <span class="tag-card__type-badge">{{ tag.type }}</span>
      <span class="tag-card__status" :class="{ 'tag-card__status--active': isActive }">
        <i class="tag-card__status-dot" :class="{ 'tag-card__status-dot--active': isActive }"></i>
        {{ isActive ? '启用中' : '已停用' }}
      </span>
    </div>
    <div v-if="poolType !== 'company'" class="tag-card__type-status2">
      <p class="tag-card__desc-id">{{ tag.caseStatusStr }}|{{ tag.contactStatusStr }}</p>
    </div>

    <div class="tag-card__right">
      <div class="tag-card__desc">
        <p class="tag-card__desc-id">ID: {{ tag.id }}</p>
        <p class="tag-card__desc-text" :title="tag.remark">{{ tag.remark }}</p>
      </div>
      <div v-if="sortMode" class="tag-card__sort-controls">
        <el-button
          class="tag-card__sort-btn--top"
          icon="el-icon-d-arrow-left"
          size="mini"
          circle
          @click="$emit('move', index, 0)"
        ></el-button>
        <el-button
          icon="el-icon-arrow-up"
          size="mini"
          circle
          :disabled="index === 0"
          @click="$emit('move', index, index - 1)"
        ></el-button>
        <el-button
          icon="el-icon-arrow-down"
          size="mini"
          circle
          @click="$emit('move', index, index + 1)"
        ></el-button>
        <el-button
          class="tag-card__sort-btn--bottom"
          icon="el-icon-d-arrow-right"
          size="mini"
          circle
          @click="$emit('move', index, 999999)"
        ></el-button>
      </div>
      <div v-else-if="(poolType === 'general' && this.$hasPerm('B008&3-2')) || (poolType !== 'general' &&this.$hasPerm('B008&3-1'))" class="tag-card__actions">
        <el-button icon="el-icon-edit" size="mini" circle @click="$emit('edit', tag)"></el-button>
        <el-button
          v-if="poolType === 'general'"
          type="primary"
          size="mini"
          plain
          @click="$emit('assign-company', tag)"
        >指定分配到公司</el-button>
        <el-button icon="el-icon-delete" size="mini" circle @click="$emit('delete', tag)"></el-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TagCard',
  props: {
    tag: { type: Object, required: true },
    index: { type: Number, required: true },
    poolType: { type: String, required: true },
    sortMode: { type: Boolean, default: false },
    parentTagName: { type: String, default: null },
    strategyConfigured: { type: Number, default: 0 },
    strategyTotal: { type: Number, default: 0 }
  },
  computed: {
    parentDisplay() {
      return this.tag.name === this.tag.labelMain
        ? '主标签：—（作为主标签）'
        : `主标签：${this.tag.labelMain}`;
    },
    sourceBadge() {
      if (this.poolType === 'company') {
        return this.tag.source === '公司自有标签' ? '公司自有标签' : '来自通用标签池';
      }
      if (this.poolType === 'business') {
        return this.tag.source === '来源公司' ? '来源公司' : '来源通用';
      }
      return '';
    },
    sourceBadgeClass() {
      if (this.poolType === 'company') {
        return this.tag.source === '公司自有标签'
          ? 'tag-card__source-badge--own'
          : 'tag-card__source-badge--general';
      }
      if (this.poolType === 'business') {
        return this.tag.source === '来源公司'
          ? 'tag-card__source-badge--biz-self'
          : 'tag-card__source-badge--biz-company';
      }
      return '';
    },
    isActive() {
      return this.tag.status === true;
    },
    strategyLabel() {
      if (!this.strategyTotal || this.strategyConfigured <= 0) return '未配置';
      if (this.strategyConfigured >= this.strategyTotal) return '全部配置';
      return '部分配置';
    },
    strategyBtnType() {
      if (!this.strategyTotal || this.strategyConfigured <= 0) return 'info';
      if (this.strategyConfigured >= this.strategyTotal) return 'success';
      return 'warning';
    }
  },
  methods: {
    // 输入优先级序号：把当前标签调整到对应优先级位置
    // 原生数字框的小箭头是"数值 ±1"（+1 序号变大＝往下），与"上移/下移"直觉相反，
    // 这里做方向反转：↑ 上移一位、↓ 下移一位；直接输入其它数字则跳到对应位置
    onJumpTo(event) {
      const current = this.index + 1;
      const rank = parseInt(event.target.value, 10);
      // 浏览器已改写了输入框的值，先复位，避免数据未变化时显示错乱
      event.target.value = current;
      if (isNaN(rank) || rank < 0) {
        this.resetRankInput();
        return;
      }
      const diff = rank - current;
      // 先按 ±1 判定单步（此时越界的 0 仍可作向下移动），再处理跳转
      if (diff === 1 || diff === -1) {
        const target = this.index - diff;
        if (target < 0) return;
        this.$emit('move', this.index, target);
        return;
      }
      if (diff === 0) return;
      // 必须同时传出当前位置与目标优先级索引，父级 jumpTo(from, target) 依赖两个参数
      this.$emit('jump-to', this.index, rank - 1);
      // 输入值等于当前序号或被父级截断时不会触发 props 变化，
      // 需手动把输入框显示同步回真实序号（目标优先级超出范围会被夹到末尾）
      this.$nextTick(this.resetRankInput);
    },
    // 将输入框显示重置为当前所在序号
    resetRankInput() {
      if (this.$refs.rankInput) {
        this.$refs.rankInput.value = this.index + 1;
      }
    },
    onDragStart(e) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', this.index);
      this.$el.classList.add('tag-card--dragging');
      this.$emit('drag-start', this.index);
    },
    onDragOver(e) {
      e.preventDefault();
      this.$el.classList.add('tag-card--drag-over');
    },
    onDrop(e) {
      e.stopPropagation();
      this.$el.classList.remove('tag-card--drag-over');
      const from = parseInt(e.dataTransfer.getData('text/plain'), 10);
      if (from !== this.index) {
        this.$emit('drag-drop', from, this.index);
      }
    },
    onDragEnd() {
      this.$el.classList.remove('tag-card--dragging');
    }
  }
};
</script>

<style lang="less" scoped>
@import '../../../assets/style/variable.less';

.tag-card {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-left: 4px solid;
  border-radius: 12px;
  padding: 16px 20px;
  transition: all 0.2s;
  margin-bottom: 12px;

  &:hover {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  }

  &--general {
    border-left-color: @primary;
  }
  &--company {
    border-left-color: #67c23a;
  }
  &--business {
    border-left-color: #a855f7;
  }

  &--sort-mode {
    cursor: move;
  }

  &--dragging {
    opacity: 0.5;
    transform: scale(0.98);
  }

  &--drag-over {
    border: 2px dashed @primary !important;
    background-color: #ecf5ff !important;
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;

    .tag-card--general & { width: 30%; }
    .tag-card--company & { width: 30%; }
    .tag-card--business & { width: 30%; }
  }

  &__rank {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  &__drag-handle {
    color: #c0c4cc;
    cursor: grab;
  }

  &__rank-input {
    width: 60px;
    text-align: center;
    font-weight: bold;
    font-size: 16px;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    padding: 2px;
  }

  &__rank-text {
    font-size: 18px;
    font-weight: bold;
    color: #c0c4cc;
  }

  &__info {
    min-width: 0;
  }

  &__name-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__name {
    font-size: 16px;
    font-weight: bold;
    color: #303133;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__priority-badge {
    font-size: 11px;
    font-weight: bold;
    background: #f4f4f5;
    color: #909399;
    padding: 2px 8px;
    border-radius: 4px;
  }

  &__source-badge {
    font-size: 11px;
    font-weight: bold;
    padding: 2px 8px;
    border-radius: 4px;

    &--general {
      background: #ecf5ff;
      color: #409eff;
    }

    &--own {
      background: #f0f9eb;
      color: #67c23a;
    }

    &--biz-company {
      background: #f4eaff;
      color: #a855f7;
    }

    &--biz-self {
      background: #fef0e6;
      color: #e6a23c;
    }
  }

  &__meta {
    display: flex;
    flex-direction: column;
    margin-top: 2px;
  }

  &__code {
    font-size: 12px;
    color: #c0c4cc;
    font-family: monospace;
    margin: 0;
  }

  &__parent {
    font-size: 11px;
    color: #c0c4cc;
  }

  &__strategy {
    width: 15%;
    display: flex;
    justify-content: center;
  }

  &__type-status {
    display: flex;
    align-items: center;
    gap: 12px;

    .tag-card--general & { width: 20%; justify-content: center; }
    .tag-card--company & { width: 30%; }
    .tag-card--business & { width: 20%; justify-content: center; }
  }

  &__type-status2 {
    display: flex;
    align-items: center;
    gap: 12px;
    justify-content: center;

    .tag-card--general & { width: 15%; }
    .tag-card--company & { width: 30%; }
    .tag-card--business & { width: 15%; }
  }

  &__type-badge {
    font-size: 12px;
    font-weight: 600;
    background: #ecf5ff;
    color: @primary;
    padding: 2px 8px;
    border-radius: 4px;
  }

  &__status {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 600;
    color: #c0c4cc;

    &--active {
      color: #67c23a;
    }
  }

  &__status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #c0c4cc;

    &--active {
      background: #67c23a;
    }
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 16px;
    justify-content: flex-end;
    flex: 1;              // 自动占满剩余宽度
    min-width: 0;

    .tag-card--general & { width: 35%; }
    .tag-card--company & { width: 25%; }
    .tag-card--business & { width: 35%; }
  }

  &__desc {
    min-width: 0;
    text-align: right;

    &-id {
      font-size: 12px;
      color: #c0c4cc;
      font-family: monospace;
      margin: 0;
    }

    &-text {
      font-size: 12px;
      color: #909399;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 200px;
      margin: 0;
    }
  }

  &__sort-controls {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__sort-btn--top .el-icon-d-arrow-left {
    display: inline-block;
    transform: rotate(90deg);
  }

  &__sort-btn--bottom .el-icon-d-arrow-right {
    display: inline-block;
    transform: rotate(90deg);
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/page/main/tagpools/tag-card.vue